import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { View, PageName } from '@/types';

interface ViewContextType {
  view: View;
  setView: (view: View) => void;
}

const ViewContext = createContext<ViewContextType | undefined>(undefined);

export const ViewProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [view, setView] = useState<View>({ page: 'Dashboard' });
  const location = useLocation();
  const isSettingViewProgrammatically = useRef(false);

  // Custom setView that tracks programmatic updates
  const setViewWithFlag = (newView: View) => {
    isSettingViewProgrammatically.current = true;
    setView(newView);
    // Reset flag after a brief delay
    setTimeout(() => {
      isSettingViewProgrammatically.current = false;
    }, 100);
  };

  // Reset view state when route changes
  useEffect(() => {
    // Skip if we're programmatically setting the view
    if (isSettingViewProgrammatically.current) {
      return;
    }

    if (location.pathname.startsWith('/admin/admin-users')) {
      if (view.page !== 'Admin Users') {
        setView({ page: 'Admin Users' });
      }
      return;
    }

    // Map routes to page names and reset subPage
    const routeToPage: Record<string, PageName> = {
      '/admin/dashboard': 'Dashboard',
      '/admin/content': 'Website Edit',
      '/admin/press-release': 'Press Release',
      '/admin/strategic-pillars': 'Strategic Pillars',
      '/admin/programmes': 'Programmes',
      '/admin/publications': 'Publications',
      '/admin/photo-gallery': 'Photo Gallery',
      '/admin/subscribers': 'Subscribers',
      '/admin/admin-users': 'Admin Users',
      '/admin/announcement': 'Announcement',
    };

    const pageName = routeToPage[location.pathname];
    if (pageName && view.page !== pageName) {
      // When navigating to a different page, always reset to list view
      // This prevents carrying over subPage and id from previous pages
      setView({ page: pageName });
    }
  }, [location.pathname, view]);

  return (
    <ViewContext.Provider value={{ view, setView: setViewWithFlag }}>
      {children}
    </ViewContext.Provider>
  );
};

export const useView = () => {
  const context = useContext(ViewContext);
  if (context === undefined) {
    throw new Error('useView must be used within a ViewProvider');
  }
  return context;
};